import { brl } from "@/lib/api";

/**
 * Comprovantes de bobina que não são a venda.
 *
 * Sangria, suprimento e movimentação de estoque viravam só um registro na tela.
 * Quem precisa deles precisa em PAPEL: a sangria vai grampeada no envelope do
 * depósito, e a conferência de estoque é assinada e arquivada. Sem papel, o
 * jeito de comprovar era abrir o sistema, o que não serve para nenhum dos dois.
 *
 * Cada comprovante sai com um AVISO de que não é documento fiscal. É o que
 * impede alguém de guardar uma sangria achando que substitui o cupom.
 *
 * (O cupom de VENDA tem o seu próprio construtor em pages/seller/pos/receipt.js
 * e repete este CSS. Não foi unificado de propósito: aquele arquivo está no
 * caminho do dinheiro e não tem teste, e a fusão seria uma mudança sem rede.)
 */

export const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

const dataHora = (iso) => new Date(iso || Date.now()).toLocaleString("pt-BR");

/**
 * A folha em si, com a mesma métrica do cupom de venda.
 *
 * Sem largura em pixels e sem recuo próprio: quem sabe a largura da bobina e o
 * quanto da borda a cabeça térmica alcança é o agente de impressão. Um padding
 * aqui somaria ao dele e estreitaria o comprovante sem motivo.
 */
function folha({ titulo, subtitulo, corpo, rodape }) {
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    @page{margin:0}
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:-apple-system,"Segoe UI",sans-serif;color:#000;font-size:12px;line-height:1.35}
    /* Nada abaixo de 11px: a 203dpi o traço engorda e o texto miúdo sai borrado. */
    .c{text-align:center}.r{text-align:right}.b{font-weight:700}
    .muted{font-size:11px}
    h1{font-size:15px}
    table{width:100%;border-collapse:collapse}
    td{overflow-wrap:anywhere;padding:1px 0}
    .r{white-space:nowrap;padding-left:6px}
    hr{border:none;border-top:1px dashed #000;margin:6px 0}
    .destaque{font-size:16px}
    .assina{margin-top:22px;border-top:1px solid #000;padding-top:3px}
  </style></head><body>
    <div class="c"><h1 class="b">Ortopédica São Paulo</h1><div class="muted">${esc(subtitulo)}</div></div>
    <hr>
    <div class="b">${esc(titulo)}</div>
    ${corpo}
    <hr>
    <div class="c muted">Documento interno — não tem valor fiscal.</div>
    ${rodape || ""}
  </body></html>`;
}

/** Linha rótulo/valor. */
const linha = (rotulo, valor) => `<tr><td>${esc(rotulo)}</td><td class="r">${esc(valor)}</td></tr>`;

/**
 * Comprovante de sangria / suprimento.
 *
 * Leva assinatura: a sangria sai da gaveta na mão de alguém, e é a assinatura
 * que liga o valor à pessoa que levou. Sem ela o papel prova o lançamento, mas
 * não a entrega.
 */
export function buildCashMovementHtml(m, { operador, sessao } = {}) {
  const sangria = m.kind === "sangria" || m.type === "sangria";
  const valor = Math.abs(Number(m.amount) || 0);

  const corpo = `
    <div class="muted">${dataHora(m.at || m.created_at)}</div>
    <hr>
    <table>
      ${linha("Tipo", sangria ? "Sangria (saída)" : "Suprimento (entrada)")}
      ${sessao ? linha("Caixa", `#${sessao}`) : ""}
      ${operador ? linha("Operador", operador) : ""}
    </table>
    <hr>
    <table>
      <tr class="b destaque"><td>${sangria ? "RETIRADO" : "ENTROU"}</td><td class="r">${sangria ? "−" : "+"} ${brl(valor)}</td></tr>
    </table>
    ${m.description || m.reason ? `<hr><div class="muted">Motivo</div><div>${esc(m.description || m.reason)}</div>` : ""}`;

  const rodape = `
    <div class="assina c muted">${sangria ? "Recebi o valor acima" : "Conferi o valor acima"}</div>`;

  return folha({
    titulo: sangria ? "Comprovante de sangria" : "Comprovante de suprimento",
    subtitulo: "Movimentação de caixa",
    corpo,
    rodape,
  });
}

/**
 * Comprovante de movimentação de estoque.
 *
 * O saldo resultante entra quando o servidor o informa: é ele que a conferência
 * confere contra a prateleira, e sem ele o papel só repete o que foi digitado.
 */
export function buildStockMovementHtml(m) {
  const SINAL = { entrada: "+", saida: "−", ajuste: "±" };
  const ROTULO = { entrada: "Entrada", saida: "Saída", ajuste: "Ajuste" };

  const corpo = `
    <div class="muted">${dataHora(m.created_at)}</div>
    <hr>
    <div class="b">${esc(m.product_name)}</div>
    ${m.variant_label && m.variant_label !== "-" ? `<div class="muted">${esc(m.variant_label)}</div>` : ""}
    <hr>
    <table>
      <tr class="b destaque"><td>${esc(ROTULO[m.type] || m.type)}</td><td class="r">${SINAL[m.type] || ""} ${Math.abs(Number(m.qty) || 0)}</td></tr>
    </table>
    <table>
      ${m.balance_after != null ? linha("Saldo após", String(m.balance_after)) : ""}
      ${m.location ? linha("Origem", m.location === "online" ? "Loja online" : "Balcão") : ""}
      ${m.user_name ? linha("Lançado por", m.user_name) : ""}
    </table>
    ${m.reason ? `<hr><div class="muted">Motivo</div><div>${esc(m.reason)}</div>` : ""}`;

  const rodape = `<div class="assina c muted">Conferido por</div>`;

  return folha({
    titulo: "Comprovante de movimentação",
    subtitulo: "Estoque",
    corpo,
    rodape,
  });
}
